import React, { useMemo, useState } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
    Typography,
} from '@mui/material'
import {
    Download,
    Eye,
    Printer,
    X,
} from 'lucide-react'
import { PDFDocument } from 'pdf-lib'

import { formatFileURL } from '../../utils/helper/appHelpers'

export default function ItemInfoV2({
    items = [],
    status,
    deliveryType= ''
}) {
    const [previewOpen, setPreviewOpen] =
        useState(false)

    const [previewUrl, setPreviewUrl] =
        useState(null)

    const [previewTitle, setPreviewTitle] =
        useState('')

    const [loading, setLoading] =
        useState(false)

    const totalPages = useMemo(() => {
        return items.reduce(
            (sum, item) =>
                sum +
                (item?.pages || 0) *
                (item?.quantity || 1),
            0
        )
    }, [items])

    const openPreview = (url, title) => {
        setPreviewUrl(url)
        setPreviewTitle(title)
        setPreviewOpen(true)
    }

    const handlePreview = item => {
        openPreview(
            formatFileURL(
                item?.fileInfo?.url
            ),
            item?.fileInfo?.name
        )
    }

    const generateMergedPdf = async (documents) => {
        const mergedPdf = await PDFDocument.create();

        for (const item of documents) {
            try {
                const fileUrl = formatFileURL(item?.fileInfo?.url);

                const response = await fetch(fileUrl);

                if (!response.ok) {
                    console.error(
                        `Failed to fetch ${item?.fileInfo?.name}`
                    );
                    continue;
                }

                const contentType =
                    response.headers.get("content-type") || "";

                const fileBytes =
                    await response.arrayBuffer();

                const copies =
                    Number(item?.quantity) || 1;

                // Handle PDF files
                if (contentType.includes("application/pdf")) {
                    const sourcePdf =
                        await PDFDocument.load(fileBytes);

                    const pageIndexes =
                        sourcePdf.getPageIndices();

                    for (let i = 0; i < copies; i++) {
                        const pages =
                            await mergedPdf.copyPages(
                                sourcePdf,
                                pageIndexes
                            );

                        pages.forEach((page) =>
                            mergedPdf.addPage(page)
                        );
                    }

                    continue;
                }

                // Handle Images
                if (contentType.startsWith("image/")) {
                    const image = contentType.includes("png")
                        ? await mergedPdf.embedPng(fileBytes)
                        : await mergedPdf.embedJpg(fileBytes);

                    const { width, height } =
                        image.scale(1);

                    for (let i = 0; i < copies; i++) {
                        const page =
                            mergedPdf.addPage([
                                595.28, // A4 Width
                                841.89, // A4 Height
                            ]);

                        const pageWidth =
                            page.getWidth();

                        const pageHeight =
                            page.getHeight();

                        const scale =
                            Math.min(
                                pageWidth / width,
                                pageHeight / height
                            ) * 0.9;

                        const scaledWidth =
                            width * scale;

                        const scaledHeight =
                            height * scale;

                        page.drawImage(image, {
                            x:
                                (pageWidth -
                                    scaledWidth) /
                                2,
                            y:
                                (pageHeight -
                                    scaledHeight) /
                                2,
                            width: scaledWidth,
                            height: scaledHeight,
                        });
                    }

                    continue;
                }

                console.warn(
                    `Unsupported file type: ${contentType}`
                );
            } catch (error) {
                console.error(
                    `Error processing ${item?.fileInfo?.name}`,
                    error
                );
            }
        }

        const mergedBytes =
            await mergedPdf.save();

        const blob = new Blob([mergedBytes], {
            type: "application/pdf",
        });

        return URL.createObjectURL(blob);
    };

    const handlePrintSingle =
        async item => {
            try {
                setLoading(true)

                const pdfUrl =
                    await generateMergedPdf(
                        [item]
                    )

                openPreview(
                    pdfUrl,
                    `${item?.fileInfo?.name} - Print Preview`
                )
            } catch (error) {
                console.error(error)
            } finally {
                setLoading(false)
            }
        }

    const handlePrintAll =
        async () => {
            try {
                setLoading(true)

                const pdfUrl =
                    await generateMergedPdf(
                        items
                    )

                openPreview(
                    pdfUrl,
                    'Print All Preview'
                )
            } catch (error) {
                console.error(error)
            } finally {
                setLoading(false)
            }
        }

    const handleBrowserPrint =
        () => {
            const iframe =
                document.getElementById(
                    'pdf-preview-frame'
                )

            iframe?.contentWindow?.focus()
            iframe?.contentWindow?.print()
        }

    return (
        <>
            {/* Header */}

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box>
                            <Typography variant="h6">
                                Documents (
                                {
                                    items.length
                                }
                                )
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Total Pages:{' '}
                                {
                                    totalPages
                                }
                            </Typography>
                        </Box>

                        {status !==
                            'COMPLETED' && (
                                <Button
                                    variant="contained"
                                    startIcon={
                                        <Printer />
                                    }
                                    onClick={
                                        handlePrintAll
                                    }
                                    disabled={
                                        loading
                                    }
                                >
                                    Print All
                                </Button>
                            )}
                    </Stack>
                </CardContent>
            </Card>

            {/* Documents */}

            <Card>
                <CardContent>
                    {items.map(
                        (
                            item,
                            index
                        ) => (
                            <Box
                                key={
                                    index
                                }
                            >
                                <Grid
                                    container
                                    spacing={
                                        2
                                    }
                                    alignItems="center"
                                >
                                    <Grid
                                        item
                                        xs={
                                            12
                                        }
                                        md={
                                            8
                                        }
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={
                                                600
                                            }
                                        >
                                            {
                                                item
                                                    ?.fileInfo
                                                    ?.name
                                            }
                                        </Typography>

                                        <Stack
                                            direction="row"
                                            spacing={
                                                2
                                            }
                                            flexWrap="wrap"
                                            useFlexGap
                                            mt={
                                                1
                                            }
                                        >
                                            <Typography variant="body2">
                                                Pages:{' '}
                                                {
                                                    item?.pages
                                                }
                                            </Typography>

                                            <Typography variant="body2">
                                                Copies:{' '}
                                                {
                                                    item?.quantity
                                                }
                                            </Typography>

                                            <Typography variant="body2">
                                                {
                                                    item
                                                        ?.printPriceInfo
                                                        ?.priceJson
                                                        ?.paperSize
                                                }
                                            </Typography>

                                            <Typography variant="body2">
                                                {
                                                    item
                                                        ?.printPriceInfo
                                                        ?.priceJson
                                                        ?.paperType
                                                }
                                            </Typography>

                                            <Typography variant="body2">
                                                {
                                                    item
                                                        ?.printPriceInfo
                                                        ?.priceJson
                                                        ?.printSide
                                                }
                                            </Typography>

                                            <Typography variant="body2">
                                                {
                                                    item
                                                        ?.printPriceInfo
                                                        ?.priceJson
                                                        ?.printingColor
                                                }
                                            </Typography>
                                        </Stack>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={
                                            12
                                        }
                                        md={
                                            4
                                        }
                                    >
                                        <Stack
                                            spacing={
                                                1
                                            }
                                            alignItems={{
                                                xs: 'flex-start',
                                                md: 'flex-end',
                                            }}
                                        >
                                            <Chip
                                                size="small"
                                                color={
                                                    Number(
                                                        item?.urgentCharges ??
                                                        0
                                                    ) >
                                                        0
                                                        ? 'success'
                                                        : 'warning'
                                                }
                                                label={
                                                   deliveryType

                                                }
                                            />

                                            <Stack
                                                direction="row"
                                                spacing={
                                                    1
                                                }
                                            >
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={
                                                        <Eye size={16} />
                                                    }
                                                    onClick={() =>
                                                        handlePreview(
                                                            item
                                                        )
                                                    }
                                                >
                                                    Preview
                                                </Button>


                                                {status !==
                                                    'COMPLETED' && (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            startIcon={
                                                                <Printer size={16} />
                                                            }
                                                            onClick={() =>
                                                                handlePrintSingle(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            Print
                                                        </Button>
                                                    )}
                                            </Stack>
                                        </Stack>
                                    </Grid>
                                </Grid>

                                {index <
                                    items.length -
                                    1 && (
                                        <Divider
                                            sx={{
                                                my: 2,
                                            }}
                                        />
                                    )}
                            </Box>
                        )
                    )}
                </CardContent>
            </Card>

            {/* Preview Dialog */}

            <Dialog
                open={
                    previewOpen
                }
                onClose={() =>
                    setPreviewOpen(
                        false
                    )
                }
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography variant="h6">
                            {
                                previewTitle
                            }
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={1}
                        >
                            <Button
                                variant="contained"
                                startIcon={
                                    <Printer />
                                }
                                onClick={
                                    handleBrowserPrint
                                }
                            >
                                Print
                            </Button>

                            <IconButton
                                onClick={() =>
                                    setPreviewOpen(
                                        false
                                    )
                                }
                            >
                                <X />
                            </IconButton>
                        </Stack>
                    </Stack>
                </DialogTitle>

                <DialogContent
                    sx={{
                        height:
                            '85vh',
                        p: 0,
                    }}
                >
                    {loading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        previewUrl && (
                            <iframe
                                id="pdf-preview-frame"
                                src={
                                    previewUrl
                                }
                                title="PDF Preview"
                                width="100%"
                                height="100%"
                                style={{
                                    border:
                                        'none',
                                }}
                            />
                        )
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}